import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Languages } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { SUPPORTED_LANGUAGES } from '../../../i18n';

export interface LanguageOption {
  code: string;
  /**
   * The language's ENDONYM ("עברית"). The language's name in the READER's
   * language is NOT here: it lives in `common:languages.<code>` and is resolved
   * with `t()` at render time, so it follows the UI language rather than being
   * frozen at module load (WI 163d34e4).
   */
  nativeName: string;
}

interface LanguagePickerProps {
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
}

// Standard language options from the app's supported-language config, which
// derives itself from `shared/config/locale.config.json § languages § values`.
const languageOptions: LanguageOption[] = SUPPORTED_LANGUAGES.map((l) => ({
  code: l.code,
  nativeName: l.nativeName,
}));

const languageMap = new Map(languageOptions.map((l) => [l.code, l]));

/**
 * Standard language picker combo. Lets the user pick a single language from the
 * app's supported languages (en / es / he). Mirrors CurrencyPicker's combobox
 * pattern so it sits consistently next to it in forms.
 */
export function LanguagePicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
}: LanguagePickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectLanguage');

  const selectedOption = useMemo(
    () => (value ? languageMap.get(value) || null : null),
    [value]
  );

  const buttonContent = selectedOption ? (
    <span className="flex items-center gap-1.5">
      <Languages className="h-4 w-4 shrink-0 opacity-70" />
      <span className="truncate">{selectedOption.nativeName}</span>
    </span>
  ) : (
    resolvedPlaceholder
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedOption && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {buttonContent}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchLanguage')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noLanguageFound')}</CommandEmpty>
              <CommandGroup>
                {languageOptions.map((option) => {
                  // The endonym is the primary line; `translatedName` is the same
                  // language named in the READER's language. They are equal for a
                  // reader viewing their own language, and then there is no second
                  // line to show. Both spellings stay in the search `value`, so
                  // typing "Hebrew" and typing "עברית" each find the row.
                  const translatedName = t(`languages.${option.code}`);
                  return (
                    <CommandItem
                      key={option.code}
                      value={`${option.code} ${translatedName} ${option.nativeName}`}
                      onSelect={() => {
                        onChange(option.code);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4',
                          value === option.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="font-medium">{option.nativeName}</span>
                      {translatedName !== option.nativeName && (
                        <span className="ms-auto text-muted-foreground text-xs truncate">
                          {translatedName}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p
          className={cn(
            'text-xs mt-1',
            error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

// Helpers for external use.
export function getLanguageByCode(code: string): LanguageOption | undefined {
  return languageMap.get(code);
}

export function getLanguageName(code: string): string {
  return languageMap.get(code)?.nativeName || code;
}

export function getAllLanguages(): LanguageOption[] {
  return languageOptions;
}
